<template>
  <div>
    <v-container fluid class="mx-12 my-6">
      <div class="card-label d-flex flex-row mb-4">
        <v-icon>mdi-clipboard-list-outline</v-icon>
        <p class="mb-0 ml-4 subtitle-1 font-weight-bold">Reservations</p>
      </div>
      <div class="d-flex align-center flex-row mb-6">
        <v-text-field
          v-model="searchString"
          solo
          flat
          append-icon="mdi-magnify"
          label="Search Tenant"
          hide-details
          @click:append="searchAction"
          @keydown.enter="searchAction"
        >
        </v-text-field>
        <v-btn
          class="font-weight-bold ml-4"
          color="primary"
          :disabled="!access.create"
          depressed
          @click="newReservation"
        >
          <v-icon class="pr-1">mdi-plus</v-icon>
          New Reservation
        </v-btn>
      </div>
      <v-card
        v-if="access.read"
        v-show="!loading"
        elevation="0"
        class="px-8 py-4"
      >
        <v-data-table
          :headers="headers"
          :items="reservations"
          :options.sync="options"
          :server-items-length="total"
          item-key="refNo"
          :loading="loading"
          loading-text="Loading... Please wait"
          no-data-text="All pending and approved Reservations will appear here."
          dense
          :footer-props="{ 'items-per-page-options': [10, 20, 50, 100] }"
        >
          <template v-slot:item="{ item }">
            <tr class="tr-head">
              <td>
                <p class="caption">{{ item.refNo }}</p>
              </td>
              <td>
                <p class="caption">{{ item.dateCreated }}</p>
              </td>
              <td>
                <p class="caption">{{ item.tenantName }}</p>
              </td>
              <td>
                <p class="caption">{{ item.notes }}</p>
              </td>
              <td>
                <p class="caption">{{ item.moveInDate }}</p>
              </td>
              <td v-if="item.status == 'payment_pending'">
                <p class="caption red--text">{{ item.status }}</p>
              </td>
              <td v-if="item.status !== 'payment_pending'">
                <p class="caption">{{ item.status }}</p>
              </td>
              <td>
                <p class="caption">{{ formatCurrency(item.reservationFee) }}</p>
              </td>
              <td>
                <div class="d-flex flex-row">
                  <v-tooltip
                    v-if="item.status === 'payment_pending' && access.update"
                    bottom
                    dark
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        color="primary"
                        x-small
                        v-on="on"
                        @click.stop="approveReservation(item)"
                      >
                        <v-icon>mdi-check-circle</v-icon>
                      </v-btn>
                    </template>
                    <span style="color: white">Approve</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="item.status === 'reserved' && leaseAccess.update"
                    bottom
                    dark
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        color="primary"
                        x-small
                        v-on="on"
                        @click.stop="moveToUnit(item)"
                      >
                        <v-icon>mdi-bed</v-icon>
                      </v-btn>
                    </template>
                    <span style="color: white">Move To Unit</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="item.status == 'payment_pending' && access.update"
                    bottom
                    dark
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        icon
                        x-small
                        v-on="on"
                        @click.stop="editReservation(item)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span style="color: white">Edit Reservation</span>
                  </v-tooltip>
                  <v-tooltip v-if="access.delete" bottom dark>
                    <template v-slot:activator="{ on }">
                      <v-btn
                        v-if="
                          item.status === 'payment_pending' ||
                          item.status === 'reserved'
                        "
                        icon
                        color="error"
                        x-small
                        v-on="on"
                        @click.stop="cancelReservation(item)"
                      >
                        <v-icon>mdi-close-circle</v-icon>
                      </v-btn>
                    </template>
                    <span style="color: white">Cancel</span>
                  </v-tooltip>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
      <v-skeleton-loader
        v-show="loading"
        ref="skeleton"
        type="table"
        loading
        class="mx-auto"
      ></v-skeleton-loader>
    </v-container>
    <ReservationToUnit ref="moveToUnit"></ReservationToUnit>
    <NewReservation ref="newReservation"></NewReservation>
    <EditReservation ref="editReservation"></EditReservation>
    <!-- <v-overlay :value="pageLoading" absolute>
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay> -->
  </div>
</template>

<script>
import ReservationToUnit from '../components/reservations/ReservationToUnit'
import NewReservation from '../components/reservations/NewReservation'
import EditReservation from '../components/reservations/EditReservation'

export default {
  components: {
    ReservationToUnit,
    NewReservation,
    EditReservation
  },
  data() {
    return {
      searchString: '',
      loading: true,
      limit: 0,
      total: 0,
      options: {},
      fab: false,
      x: 0,
      y: 0,
      pageLoading: true
    }
  },
  computed: {
    reservations() {
      return this.$store.getters['reservations/getReservations']
    },
    headers() {
      return this.$store.getters['reservations/getHeaders']
    },
    access() {
      return this.$root.$roleAccess('reservations').access
    },
    leaseAccess() {
      return this.$root.$roleAccess('lease').access
    }
  },
  watch: {
    options: {
      handler() {
        this.$store.dispatch('reservations/fetchReservationsAction', {
          page: this.options.page,
          limit: this.options.itemsPerPage
        })
      }
    }
  },
  mounted() {
    this.$store
      .dispatch('reservations/fetchReservationsAction', {
        page: 1,
        limit: this.options.itemsPerPage
      })
      .then((data) => {
        this.loading = false
        this.pageLoading = false
        this.limit = data.limit
        this.total = data.count
      })
  },
  methods: {
    async approveReservation(reservation) {
      if (
        await this.$root.$confirm({
          title: 'Approve Reservation',
          message: `Are you sure ${reservation.tenantName} has paid the reservation fee of Php${reservation.reservationFee}?`
        })
      ) {
        this.pageLoading = true
        this.$store
          .dispatch('reservations/approveReservationAction', {
            reservationId: reservation.reservationId
          })
          .then(async () => {
            await this.$store.dispatch('reservations/fetchReservationsAction', {
              page: this.options.page,
              limit: this.options.itemsPerPage
            })

            this.pageLoading = false
            this.$root
              .$confirm({
                title: 'Approve Reservation',
                message: `The reservation has been approved.`,
                reconfirmOnly: true,
                confirmText: 'OK'
              })
              .then(() => {
                // this.$router.go()
                window.location.reload()
              })
          })
      }
    },
    async moveToUnit(reservation) {
      this.$refs.moveToUnit.open({
        tenantId: reservation.tenantId,
        reservationId: reservation.reservationId
      })
    },
    async newReservation() {
      if (await this.$refs.newReservation.open()) {
        await this.$store.dispatch('reservations/fetchReservationsAction', {
          page: this.options.page,
          limit: this.options.itemsPerPage
        })
        // this.$router.go()
        window.location.reload()
      }
    },
    async cancelReservation(reservation) {
      if (
        await this.$root.$confirm({
          title: 'Cancel Reservation',
          message: `Are you sure you want to cancel ${reservation.tenantName}'s reservation?`
        })
      ) {
        this.$store
          .dispatch('reservations/cancelReservationAction', {
            reservationId: reservation.reservationId
          })
          .then(() => {
            this.$store.dispatch('reservations/fetchReservationsAction', {
              page: this.options.page,
              limit: this.options.itemsPerPage
            })

            this.$root.$confirm({
              title: 'Cancel Reservation',
              message: `The reservation has been canceled.`,
              reconfirmOnly: true,
              confirmText: 'OK'
            })
          })
      }
    },
    tempClick() {
      //eslint-disable-next-line
      console.log('click')
    },
    async editReservation(selected) {
      if (await this.$refs.editReservation.open(selected)) {
        this.$store.dispatch('reservations/fetchReservationsAction', {
          page: this.options.page,
          limit: this.options.itemsPerPage
        })
      }
    },
    searchAction() {},
    formatCurrency(value) {
      if (value == null) return ''
      return parseFloat(value / 100).toLocaleString('en-PH', {
        style: 'currency',
        currency: 'PHP'
      })
    }
  }
}
</script>

<style scoped>
.v-speed-dial {
  bottom: 5vw;
  right: 3vw;
}
.v-card {
  border-radius: 12px !important;
}
.v-input {
  border-radius: 10px;
}
.v-btn {
  border-radius: 20px;
}
table tbody tr td {
  /*border: none !important;
    border-bottom: none !important;*/
  border-color: #eeeeee !important;
}
p {
  margin-bottom: 0;
}
.v-menu__content {
  border-radius: 20px !important;
}
</style>
